import React, { useEffect, useState, createContext, useContext } from 'react';
/**
 * Window Management Context
 * Handles window state, positioning, z-index and mobile responsiveness
 */

import { WindowState, WindowContextValue, WindowPosition, WindowSize } from '../types/window';

// Tijdelijke interface voor de update van de grootte (ervan uitgaande dat je dit in je types/window.ts zou definiëren)
interface ResizeUpdate {
  newWidth: number;
  newHeight: number;
  direction: string; // Bijv. 'n', 'se', 'w'
}

// Pas dit aan in je 'WindowContextValue' type in '../types/window.ts'
// Zorg ervoor dat de signatuur in je type definitie overeenkomt met de implementatie hieronder:
// updateWindowSize: (id: string, update: ResizeUpdate) => void;

const WindowContext = createContext<WindowContextValue | undefined>(undefined);
interface WindowProviderProps {
  children: React.ReactNode;
}
export function WindowProvider({
  children
}: WindowProviderProps) {
  const [windows, setWindows] = useState<WindowState[]>([]);
  const [highestZIndex, setHighestZIndex] = useState(100);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile/tablet
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Generate unique window ID
  const generateWindowId = () => {
    return `window-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  };

  // Get default window position (cascade effect)
  const getDefaultPosition = (): WindowPosition => {
    const offset = windows.length * 40;
    return {
      x: Math.min(100 + offset, window.innerWidth - 400),
      y: Math.min(80 + offset, window.innerHeight - 300)
    };
  };

  // Get default window size
  const getDefaultSize = (): WindowSize => {
    if (isMobile) {
      return {
        width: window.innerWidth,
        height: window.innerHeight
      };
    }
    return {
      width: Math.min(800, window.innerWidth - 200),
      height: Math.min(600, window.innerHeight - 200)
    };
  };
  const openWindow = (appId: string, title: string, projectId?: string) => {
    // Check if window for this app already exists
    const existingWindow = windows.find(w => w.appId === appId && (!projectId || w.projectId === projectId));
    if (existingWindow) {
      // Focus existing window and restore if minimized
      if (existingWindow.isMinimized) {
        minimizeWindow(existingWindow.id);
      }
      focusWindow(existingWindow.id);
      return;
    }
    // Create new window with highest zIndex + 1
    const newZIndex = highestZIndex + 1;
    setHighestZIndex(newZIndex);
    const newWindow: WindowState = {
      id: generateWindowId(),
      appId,
      title,
      position: getDefaultPosition(),
      size: getDefaultSize(),
      zIndex: newZIndex,
      isMinimized: false,
      isMaximized: isMobile,
      projectId
    };
    setWindows(prev => [...prev, newWindow]);
  };
  const closeWindow = (id: string) => {
    setWindows(prev => prev.filter(w => w.id !== id));
  };
  const minimizeWindow = (id: string) => {
    setWindows(prev => prev.map(w => w.id === id ? {
      ...w,
      isMinimized: !w.isMinimized
    } : w));
  };
  const maximizeWindow = (id: string) => {
    setWindows(prev => prev.map(w => w.id === id ? {
      ...w,
      isMaximized: !w.isMaximized,
      // Reset position when maximizing
      position: !w.isMaximized ? {
        x: 0,
        y: 0
      } : w.position
    } : w));
  };
  const focusWindow = (id: string) => {
    const targetWindow = windows.find(w => w.id === id);
    if (!targetWindow) return;
    // Check if already has highest zIndex
    const maxZIndex = Math.max(...windows.map(w => w.zIndex));
    if (targetWindow.zIndex < maxZIndex) {
      const newZIndex = highestZIndex + 1;
      setHighestZIndex(newZIndex);
      setWindows(prev => prev.map(w => w.id === id ? {
        ...w,
        zIndex: newZIndex
      } : w));
    }
  };
  const updateWindowPosition = (id: string, position: WindowPosition) => {
    setWindows(prev => prev.map(w => w.id === id ? {
      ...w,
      position
    } : w));
  };

  /**
   * NIEUWE LOGICA VOOR RESIZING: Past zowel grootte als positie aan.
   * Accepteert de berekende nieuwe breedte/hoogte en de richting van de sleep.
   */
  const updateWindowSize = (id: string, {
    newWidth,
    newHeight,
    direction
  }: ResizeUpdate) => {
    setWindows(prevWindows => prevWindows.map(w => {
      if (w.id !== id) return w;
      let width = w.size.width;
      let height = w.size.height;
      let newX = w.position.x;
      let newY = w.position.y;
      const MIN_WIDTH = 200;
      const MIN_HEIGHT = 100;

      // Bereken de delta (het verschil in grootte/positie)
      if (direction.includes('n')) {
        // Noord (boven)
        // Als we omhoog slepen, moet de hoogte groter worden en de Y-positie kleiner
        const deltaY = height - newHeight; // De verandering in hoogte
        height = Math.max(height - deltaY, MIN_HEIGHT);
        newY = w.position.y - (height - w.size.height); // Pas Y-positie aan
      } else if (direction.includes('s')) {
        // Zuid (onder)
        height = Math.max(newHeight, MIN_HEIGHT);
      }
      if (direction.includes('w')) {
        // West (links)
        // Als we naar links slepen, moet de breedte groter worden en de X-positie kleiner
        const deltaX = width - newWidth; // De verandering in breedte
        width = Math.max(width - deltaX, MIN_WIDTH);
        newX = w.position.x - (width - w.size.width); // Pas X-positie aan
      } else if (direction.includes('e')) {
        // Oost (rechts)
        width = Math.max(newWidth, MIN_WIDTH);
      }

      // Zorg voor de minimale grootte na alle berekeningen
      width = Math.max(width, MIN_WIDTH);
      height = Math.max(height, MIN_HEIGHT);

      // Indien het slepen te ver is gegaan (voorbij de minimale grootte), corrigeer de positie
      if (width === MIN_WIDTH && direction.includes('w')) {
        newX = w.position.x + (w.size.width - MIN_WIDTH);
      }
      if (height === MIN_HEIGHT && direction.includes('n')) {
        newY = w.position.y + (w.size.height - MIN_HEIGHT);
      }
      return {
        ...w,
        position: {
          x: newX,
          y: newY
        },
        size: {
          width,
          height
        }
      };
    }));
  };
  const closeAllWindows = () => {
    setWindows([]);
  };
  const value: WindowContextValue = {
    windows,
    openWindow,
    closeWindow,
    minimizeWindow,
    maximizeWindow,
    focusWindow,
    updateWindowPosition,
    // @ts-ignore: Pas dit type aan in je definitie
    updateWindowSize,
    closeAllWindows,
    isMobile
  };
  return <WindowContext.Provider value={value} data-id="element-982">{children}</WindowContext.Provider>;
}
export function useWindows() {
  const context = useContext(WindowContext);
  if (!context) {
    throw new Error('useWindows must be used within WindowProvider');
  }
  return context;
}